import { FC, useCallback, useEffect, useState } from "react";

import { RowWithEmbeddedMetadata, TextSchemaItem } from "@data-root/types";
import { inputGuard } from "../../hocs/guard";
import { SchemaItemProps, View, useField } from "@data-root/react";
import { Input, Skeleton, Typography } from "antd";
import { Remarks } from "../remarks";

const { Title } = Typography;

export const TextInput: FC<SchemaItemProps<TextSchemaItem>> = inputGuard(
  ({ item }) => {
    const {
      loaded,
      displayName,
      placeholder,
      editable,
      show,
      defaultValue,
      subscribe,
      setValue: _setValue,
      remarks,
    } = useField<string>(item);

    const [value, setValue] = useState<string | null>(defaultValue);

    const handleSetValue = useCallback(
      (value: string | null) => {
        if (value === null) {
          setValue("");
          _setValue("", "");
          return;
        }

        setValue(value);
        _setValue(value, value);
      },

      [_setValue]
    );

    const onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
      (event) => {
        handleSetValue(event.target.value);
      },
      [handleSetValue]
    );

    useEffect(() => {
      const callback = (
        next: RowWithEmbeddedMetadata,
        options?: {
          init?: boolean;
          evaluation?: boolean;
        }
      ) => {
        if (options?.init) {
          setValue(next[item.id] as string);
          return;
        }
        if (!options?.evaluation) return;

        setValue(next[item.id] as string);
      };

      const unsubscribe = subscribe(callback);

      return () => {
        unsubscribe();
      };
    }, [item.id, subscribe]);

    if (!show) return null;

    return (
      <View style={{ width: "100%" }}>
        <Title level={5}>{displayName}</Title>

        {loaded ? (
          <Input
            readOnly={!editable}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
          />
        ) : (
          <Skeleton.Input block active />
        )}

        <Remarks texts={remarks} />
      </View>
    );
  }
);
